// Shuffle animation templates for the temporary spinning winner reveal layer.
import { t } from '../../shared/config/translations'

type FlowParticle = {
	type: 'dot' | 'line' | 'meteor'
	x: number
	y: number
	width?: number
	speed: number
	opacity: number
	dot?: boolean
}

const FLOW_PARTICLES: FlowParticle[] = [
	{ type: 'line', x: 8, y: 4, width: 13, speed: 46, opacity: 0.2 },
	{ type: 'dot', x: 72, y: 7, speed: 54, opacity: 0.34 },
	{
		type: 'line',
		x: 54,
		y: 11,
		width: 17,
		speed: 62,
		opacity: 0.24,
		dot: true,
	},
	{ type: 'meteor', x: 31, y: 15, width: 6, speed: 168, opacity: 0.9 },
	{
		type: 'line',
		x: 2,
		y: 19,
		width: 15,
		speed: 72,
		opacity: 0.3,
		dot: true,
	},
	{ type: 'line', x: 81, y: 21, width: 19, speed: 80, opacity: 0.4 },
	{ type: 'dot', x: 66, y: 24, speed: 66, opacity: 0.3 },
	{
		type: 'line',
		x: 14,
		y: 27,
		width: 24,
		speed: 92,
		opacity: 0.44,
		dot: true,
	},
	{ type: 'meteor', x: 86, y: 29, width: 7, speed: 190, opacity: 0.92 },
	{
		type: 'line',
		x: 48,
		y: 31,
		width: 18,
		speed: 78,
		opacity: 0.34,
		dot: true,
	},
	{ type: 'dot', x: 5, y: 34, speed: 96, opacity: 0.66 },
	{ type: 'line', x: 76, y: 36, width: 23, speed: 88, opacity: 0.42 },
	{ type: 'meteor', x: 13, y: 39, width: 9, speed: 176, opacity: 0.86 },
	{ type: 'line', x: 35, y: 41, width: 14, speed: 68, opacity: 0.26 },
	{
		type: 'line',
		x: 89,
		y: 44,
		width: 16,
		speed: 104,
		opacity: 0.46,
		dot: true,
	},
	{ type: 'dot', x: 23, y: 47, speed: 74, opacity: 0.45 },
	{ type: 'line', x: 1, y: 50, width: 20, speed: 98, opacity: 0.4 },
	{ type: 'meteor', x: 61, y: 52, width: 5, speed: 204, opacity: 0.94 },
	{
		type: 'line',
		x: 70,
		y: 55,
		width: 17,
		speed: 82,
		opacity: 0.32,
		dot: true,
	},
	{ type: 'dot', x: 96, y: 57, speed: 112, opacity: 0.55 },
	{
		type: 'line',
		x: 18,
		y: 60,
		width: 21,
		speed: 90,
		opacity: 0.42,
		dot: true,
	},
	{ type: 'meteor', x: 37, y: 63, width: 8, speed: 184, opacity: 0.84 },
	{ type: 'line', x: 82, y: 65, width: 14, speed: 70, opacity: 0.28 },
	{ type: 'dot', x: 56, y: 68, speed: 64, opacity: 0.32 },
	{
		type: 'line',
		x: 4,
		y: 71,
		width: 18,
		speed: 86,
		opacity: 0.38,
		dot: true,
	},
	{ type: 'meteor', x: 73, y: 74, width: 6, speed: 166, opacity: 0.8 },
	{ type: 'line', x: 42, y: 77, width: 22, speed: 76, opacity: 0.32 },
	{ type: 'dot', x: 91, y: 80, speed: 94, opacity: 0.4 },
	{ type: 'line', x: 11, y: 83, width: 14, speed: 60, opacity: 0.3 },
	{
		type: 'line',
		x: 65,
		y: 86,
		width: 18,
		speed: 72,
		opacity: 0.28,
		dot: true,
	},
	{ type: 'meteor', x: 29, y: 89, width: 5, speed: 152, opacity: 0.7 },
	{ type: 'dot', x: 52, y: 92, speed: 58, opacity: 0.28 },
]

/** Renders the outer animation layer container. */
export function shuffleAnimationLayerContainerTemplate(): string {
	return `<div id="shuffle-animation-layer" class="is-entering">${shuffleAnimationLayerTemplate()}</div>`
}

/** Renders the overlay-level field that moves behind the shuffle content. */
export function shuffleFlowFieldTemplate(): string {
	return `
		<div class="shuffle-flow-field" aria-hidden="true">
			${FLOW_PARTICLES.map(renderFlowParticle).join('')}
		</div>
	`
}

/** Renders the card shown inside the shuffle animation layer. */
export function shuffleAnimationLayerTemplate(): string {
	return `
        <div class="shuffle-card">
            <div class="shuffle-viewport" aria-hidden="true">
                <div class="shuffle-carousel-track"></div>
            </div>
            <button
                id="shuffle-stop-button"
                class="shuffle-stop-button btn secondary"
                type="button"
            >
                <svg><use href="#pepicons-pop-times"></use></svg>
                ${t('shuffle.stopAnimation')}
            </button>
        </div>
    `
}

function renderFlowParticle(particle: FlowParticle): string {
	const classNames = [
		'shuffle-flow-particle',
		`is-${particle.type}`,
		particle.dot ? 'has-dot' : '',
	]
		.filter(Boolean)
		.join(' ')
	const width = particle.width ?? (particle.type === 'dot' ? 0.3 : 5)

	return `<span
		class="${classNames}"
		data-flow-x="${particle.x}"
		data-flow-speed="${particle.speed}"
		style="--flow-x: ${particle.x}%; --flow-y: ${particle.y}%; --flow-width: ${width}%; --flow-opacity: ${particle.opacity};"
	></span>`
}
